/**
 * PhotoFeed component - infinite scrolling story feed with pull-to-refresh
 * Updated to use stories instead of individual photos
 */

import { useEffect, useRef, useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { StoryCard } from '../stories/StoryCard';
import { useStoryFeed } from '../../services/stories';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';

interface PhotoFeedProps {
  onPhotoClick?: (storyId: string) => void;
  onUserClick?: (userId: string) => void;
}

export const PhotoFeed = ({ onPhotoClick, onUserClick }: PhotoFeedProps) => {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
    refetch,
    isRefetching,
  } = useStoryFeed();

  const loadMoreRef = useRef<HTMLDivElement>(null);
  const [pullDistance, setPullDistance] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const touchStartY = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (!loadMoreRef.current || !hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(loadMoreRef.current);

    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Pull-to-refresh handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    if (containerRef.current?.scrollTop === 0) {
      touchStartY.current = e.touches[0].clientY;
      setIsPulling(true);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isPulling || containerRef.current?.scrollTop !== 0) {
      setIsPulling(false);
      setPullDistance(0);
      return;
    }

    const currentY = e.touches[0].clientY;
    const distance = Math.max(0, currentY - touchStartY.current);

    // Cap at 100px
    setPullDistance(Math.min(distance, 100));
  };

  const handleTouchEnd = async () => {
    if (pullDistance > 60) {
      // Trigger refresh
      await refetch();
    }

    setIsPulling(false);
    setPullDistance(0);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Loading stories...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <Alert variant="destructive" className="mx-auto max-w-2xl">
        <AlertDescription>Failed to load stories. {error?.message}</AlertDescription>
      </Alert>
    );
  }

  const stories = data?.pages.flatMap((page) => page.items) || [];

  if (stories.length === 0) {
    return (
      <div className="mx-auto flex max-w-md flex-col items-center justify-center gap-4 py-12 text-center">
        <div className="text-6xl">📷</div>
        <h3 className="text-lg font-semibold">No stories yet</h3>
        <p className="text-sm text-muted-foreground">
          Start sharing your family moments by uploading your first photos!
        </p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="relative mx-auto w-full max-w-2xl space-y-6"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      data-testid="photo-feed"
    >
      {/* Pull-to-refresh indicator */}
      {isPulling && pullDistance > 0 && (
        <div
          className="absolute left-0 right-0 top-0 flex items-center justify-center transition-all"
          style={{ height: `${pullDistance}px` }}
          data-testid="refresh-indicator"
        >
          <RefreshCw
            className={`h-6 w-6 text-muted-foreground ${pullDistance > 60 ? 'animate-spin' : ''}`}
            style={{
              transform: `rotate(${pullDistance * 3.6}deg)`,
            }}
          />
        </div>
      )}

      {isRefetching && !isPulling && (
        <div className="py-2 text-center" data-testid="refresh-indicator">
          <RefreshCw className="inline h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      )}

      {stories.map((story) => (
        <StoryCard
          key={story.storyId}
          story={story}
          onStoryClick={onPhotoClick}
          onUserClick={onUserClick}
        />
      ))}

      {/* Infinite scroll trigger */}
      {hasNextPage && (
        <div ref={loadMoreRef} className="flex justify-center py-8">
          {isFetchingNextPage ? (
            <div className="flex items-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Loading more...</span>
            </div>
          ) : (
            <Button variant="outline" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
              Load More
            </Button>
          )}
        </div>
      )}

      {/* End of feed message */}
      {!hasNextPage && stories.length > 0 && (
        <div className="py-8 text-center text-sm text-muted-foreground">
          You've reached the end! 🎉
        </div>
      )}
    </div>
  );
};
